/**
 * Markup and gross margin: two ways of saying the same uplift.
 *
 * Markup is measured against COST — 50% markup on $100 sells at $150.
 * Gross margin is measured against PRICE — 50% margin on $100 sells at $200.
 *
 * They are the same money stated against different denominators, and confusing
 * one for the other silently underprices work. Anywhere either can be typed,
 * the basis must be stated and both must round-trip to the same price.
 */

export type UpliftBasis = 'MARKUP' | 'MARGIN';

const num = (value: number | null | undefined): number =>
  Number.isFinite(value) ? (value as number) : 0;

const round = (value: number): number => Math.round(value * 100) / 100;

/** Gross margin % that a markup % produces. */
export function marginFromMarkup(markupPercent: number): number {
  const markup = num(markupPercent);
  // -100% markup means selling at zero; there is no margin to state.
  if (markup <= -100) return 0;
  return round((markup / (100 + markup)) * 100);
}

/** Markup % needed to reach a gross margin %. */
export function markupFromMargin(marginPercent: number): number {
  const margin = num(marginPercent);
  // 100% margin means cost is zero — no finite markup gets there from a real
  // cost, so it is refused rather than returned as Infinity.
  if (margin >= 100) return 0;
  return round((margin / (100 - margin)) * 100);
}

/** The price a cost reaches under an uplift stated either way. */
export function priceFromUplift(cost: number, value: number, basis: UpliftBasis): number {
  const base = num(cost);
  const percent = basis === 'MARGIN' ? markupFromMargin(value) : num(value);
  return round(base * (1 + percent / 100));
}

/** What an uplift adds, over and above cost. */
export function upliftAmount(cost: number, value: number, basis: UpliftBasis): number {
  return round(priceFromUplift(cost, value, basis) - num(cost));
}

/**
 * A surcharge stated as a flat amount, a markup, or a gross margin.
 *
 * FLAT is dollars and ignores the base entirely; the other two are percentages
 * of the base, differing only in which denominator they are measured against.
 */
export type SurchargeBasis = 'FLAT' | 'MARKUP' | 'MARGIN';

export function surchargeAmount(base: number, value: number, basis: SurchargeBasis): number {
  if (basis === 'FLAT') return round(num(value));
  return upliftAmount(base, value, basis);
}
